<template>
  <form class="select-address row"
        @submit.prevent="submit">
    <div class="col-md-6">
      <h3>Step 2: Find your address</h3>
      <div class="register-intro">
        If your school or organisation does not appear in the list,
        you may need to go back to Step 1 to select a different user type
      </div>
      <PafSearch
        v-if="pafSearch && !showManualAddress"
        v-model="address"
        :user-type="userType"
        :show-validation="showValidation">
      </PafSearch>
      <OrgSearch
        v-else-if="orgSearch && !showManualAddress"
        v-model="address"
        :user-type="userType"
        :show-validation="showValidation">
      </OrgSearch>

      <div v-if="allowManualAddress"
           class="select-address__manual">
        <b-form-checkbox
          v-if="showManualAddressToggle"
          v-model="showManualAddress"
          class="select-address__manual-toggle"
          type="checkbox">
          My address is not shown
        </b-form-checkbox>

        <ManualAddress
          v-show="showManualAddress || !showManualAddressToggle"
          v-model="address"
          :user-type="userType"
          :show-validation="showValidation">
        </ManualAddress>
      </div>
      <div v-else
           class="select-address__manual-disallowed">
        {{ disallowedMessage }}
      </div>
    </div>

    <div class="col-12 registration-buttons">
      <button type="button"
              class="btn btn-accent-6 btn-rounded"
              @click="onGoBack">
        Go back
      </button>
      <button class="btn btn-accent-6 btn-rounded"
              type="submit">
        Proceed
      </button>
    </div>
  </form>
</template>

<script>
import { get } from "lodash";
import PafSearch from "~/Components/Forms/AddressSearch/PafSearch.vue";
import OrgSearch from "~/Components/Forms/AddressSearch/OrgSearch.vue";
import ManualAddress from "~/Components/Forms/AddressSearch/ManualAddress.vue";

export default {
  components: {
    PafSearch,
    OrgSearch,
    ManualAddress
  },

  props: {
    /**
     * V-Model. NB this component only supports one way binding, data cannot be passed in
     */
    value: {
      type: Object,
      default: () => {}
    },

    /**
     * The selected user type
     */
    userType: {
      type: Object,
      default: () => {}
    }
  },

  data () {
    return {
      showValidation: false,
      showManualAddress: false,
      address: {}
    };
  },

  computed: {
    /**
     * User much seach PAF for their address
     */
    pafSearch () {
      return get( this.userType, "allowExternalOrgSearch", false );
    },

    /**
     * User must search Spirti for their address
     */
    orgSearch () {
      return get( this.userType, "allowOrgSearch", false );
    },

    /**
     * Whether or not the user can search for their address some how
     */
    canSearch () {
      return this.orgSearch || this.pafSearch;
    },

    /**
     * Show the "My Address is not shown" option or not
     */
    showManualAddressToggle () {
      return this.allowManualAddress && this.canSearch;
    },

    /**
     * Whether or not we are allowed to add a manual address
     */
    allowManualAddress () {
      return get( this.userType, "allowManualOrgEntry", false );
    },

    /**
     * If we are not allowed to add a manual address this message will be displayed
     */
    disallowedMessage () {
      return get( this.userType, "manualOrgEntryDisallowedUserMessage", null );
    },

    /**
     * Whether we need to organisation name
     */
    askForOrgName () {
      return get( this.userType, "askForOrgName", false );
    }
  },

  watch: {
    /**
     * When the address is changed, pass it to the parent
     */
    address: {
      deep: true,
      handler ( val ) {
        this.$emit( "input", val );
      }
    },
    /**
     * Reset the manual address state if org search is set to true
     */
    orgSearch: {
      immediate: true,
      handler ( val ) {
        if ( val ) {
          this.showManualAddress = false;
        }
      }
    }
  },

  methods: {
    /**
     * The form has been submitted
     */
    submit () {
      this.showValidation = true;

      if ( this.isValid() ) {
        this.$emit( "proceed" );
      }
    },

    /**
     * Work out if the address is valid
     */
    isValid () {
      let isValid = false;

      if ( this.orgSearch && !this.showManualAddress ) {
        // For Org Searches having the ID is enough
        isValid = !!this.address.id;
      } else {
        // For everything else we should have a complete address
        const addressLine = this.address.address1 || this.address.address2 || this.address.address3;
        // eslint-disable-next-line
        isValid =
          addressLine && this.address.town && this.address.postcode && this.address.countryId;

        // If we are entering a manual address and we need an Organisation Name, make sure it is
        // supplied
        if (
          this.allowManualAddress
          && this.askForOrgName
          && ( this.showManualAddress || !this.showManualAddressToggle )
        ) {
          isValid = isValid && this.address.orgName;
        }
      }

      return isValid;
    },
    onGoBack () {
      this.$emit( "goBack" );
    }
  }
};
</script>

<style lang="less" scoped>
.select-address__manual {
  .select-address__manual-toggle {
    margin: 20px auto;
  }
}
</style>
